
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Category, CategoryFormData } from "./types";
import CategoryKeywordManager from "./CategoryKeywordManager";

interface CategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  formData: CategoryFormData;
  setFormData: (data: CategoryFormData) => void;
  keywordsInput: string;
  setKeywordsInput: (value: string) => void;
  handleAddKeyword: () => void;
  handleRemoveKeyword: (keyword: string) => void;
  handleSaveCategory: () => void;
  resetForm: () => void;
  categories: Category[];
  isEditMode?: boolean;
}

const CategoryFormDialog = ({
  open,
  onOpenChange,
  title,
  description,
  formData,
  setFormData,
  keywordsInput,
  setKeywordsInput,
  handleAddKeyword,
  handleRemoveKeyword,
  handleSaveCategory,
  resetForm,
  categories,
  isEditMode = false
}: CategoryFormDialogProps) => {
  
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  };
  
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      slug: formData.slug || generateSlug(name)
    });
  };
  
  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      slug: generateSlug(e.target.value)
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="name" className="text-sm font-medium">Name <span className="text-red-500">*</span></label>
            <Input
              id="name"
              value={formData.name}
              onChange={handleNameChange}
              placeholder="e.g. Product Strategy"
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="slug" className="text-sm font-medium">Slug <span className="text-red-500">*</span></label>
            <Input
              id="slug"
              value={formData.slug}
              onChange={handleSlugChange}
              placeholder="e.g. product-strategy"
            />
            <p className="text-xs text-muted-foreground">
              Used in URLs. Auto-generated from name but can be customized.
            </p>
          </div>
          
          <div className="space-y-2">
            <label htmlFor="description" className="text-sm font-medium">Description</label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Describe the purpose of this category"
              rows={3}
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="parent" className="text-sm font-medium">Parent Category</label>
            <Select
              value={formData.parent_id !== null ? formData.parent_id : "none"}
              onValueChange={(value) => setFormData({...formData, parent_id: value === "none" ? null : value})}
            >
              <SelectTrigger>
                <SelectValue placeholder="None (Root Category)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (Root Category)</SelectItem>
                {categories
                  .filter(cat => !isEditMode || cat.id !== formData.id) // Exclude self when editing
                  .map((cat) => (
                    <SelectItem 
                      key={cat.id} 
                      value={cat.id}
                      disabled={isEditMode && cat.parent_id === formData.id} // Prevent circular references
                    >
                      {cat.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label htmlFor="icon" className="text-sm font-medium">Icon</label>
            <Input
              id="icon"
              value={formData.icon}
              onChange={(e) => setFormData({...formData, icon: e.target.value})}
              placeholder="lucide-react icon name (e.g. 'layers')"
            />
            <p className="text-xs text-muted-foreground">
              Enter a Lucide icon name (see lucide.dev for available icons)
            </p>
          </div>
          
          <CategoryKeywordManager
            keywords={formData.keywords}
            keywordsInput={keywordsInput}
            setKeywordsInput={setKeywordsInput}
            handleAddKeyword={handleAddKeyword}
            handleRemoveKeyword={handleRemoveKeyword}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => {
            resetForm();
            onOpenChange(false);
          }}>
            Cancel
          </Button>
          <Button onClick={handleSaveCategory}>
            {isEditMode ? "Save Changes" : "Save Category"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CategoryFormDialog;
